/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect } from "react";
import { toDate, toIsoDateString } from "../utils/shiftDate";
import { extractShiftLocalTime } from "../utils/shiftTimezone";
import { alpha } from '@mui/material/styles';
import { Table, TableHead, TableRow, TableCell, TableBody, TableContainer, TableFooter, Paper, Typography, Box, Button, Link, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import Tooltip from '@mui/material/Tooltip';
import EmployeeQuickView from './EmployeeQuickView';
import { useLocations } from "../hooks/useFirestoreData";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';
import { useAvailability, isEmployeeAvailable } from "../hooks/useAvailability";
import useAssignmentsForLocation from "../hooks/useAssignmentsForLocation";
import {
  DndContext,
  DragOverlay,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
// removed sortable imports — this component uses primitive draggable/droppable

// Debug UI disabled for production (no runtime flag needed)

// Toggle pointer capture during drag: only used for touch input in the
// hybrid implementation below. Leave true to give touch users pointer
// lifecycle robustness; mouse drags are unaffected.
const ENABLE_POINTER_CAPTURE = true;

function formatTime(t) {
  if (!t) return "";
  const [hourStr, minuteStr] = t.split(":");
  let h = parseInt(hourStr, 10);
  const m = parseInt(minuteStr || '0', 10);
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  const minutePart = m === 0 ? '' : `:${m.toString().padStart(2, '0')}`;
  return `${h}${minutePart}${ampm}`;

}

// Draggable Shift Component
function DraggableShift({ shift, roleMap, isDragOverlay = false, disabled = false, isWeekPublished = false, activeShiftId = null, onEditClick = null, openDialogImmediate: localOpenDialogImmediate = null, parentIsDragging = false, forceEndDrag = null, forceSuspendDrag = null, overlayPending = false, availability = [], conflictMode = false }) {

  // When rendering inside a DragOverlay do NOT initialize useDraggable —
  // make the overlay copy purely presentational. Calling useDraggable for
  // the overlay can cause incorrect positioning/offsets.
  // Call hook unconditionally (rules of hooks). We'll only apply the
  // returned refs/listeners when not rendering an overlay copy.
  const _draggable = useDraggable({
    id: shift.id,
    data: { type: 'shift', shift },
    disabled,
  });
  const attributes = _draggable.attributes || {};
  const listeners = _draggable.listeners || {};
  const setNodeRef = _draggable.setNodeRef || (() => {});
  const localIsDragging = !!_draggable.isDragging;
  // Combine local draggable state with parent/global dragging state so
  // we can reliably know when a drag is in progress (dnd-kit timing can
  // allow a small window where localIsDragging is false but the parent
  // has already started a drag). Parent state is passed in via
  // `parentIsDragging` prop.
  const isDragging = parentIsDragging || localIsDragging;

  // When rendering inside a DragOverlay we must not rely on the sortable
  // transform/attributes — DragOverlay positions the node for us. Use the
  // sortable values only for in-grid items.
  // Availability: determine whether this shift falls outside employee availability
  let isOutsideAvailability = false;
  try {
    // Use timezone-safe extraction with legacy fallback
    const { localDate, startTime: sTime, endTime: eTime } = extractShiftLocalTime(shift);
    const shiftDate = toDate(localDate);
    const dayOfWeek = shiftDate.getDay();
    if (typeof isEmployeeAvailable === 'function') {
      isOutsideAvailability = !isEmployeeAvailable(Array.isArray(availability) ? availability : [], shift.employeeId, dayOfWeek, sTime, eTime, shift.shiftDate || shift.shiftDate?.toDate?.());
    }
  } catch (err) {
    isOutsideAvailability = false;
  }

  const baseStyle = {
  // While dragging: dim other shifts slightly for context. Hide the
  // original active shift so the DragOverlay copy is the single visual
  // element that follows the pointer.
  // Let conflictMode control the base opacity so it cannot be overridden by sx
  opacity: (conflictMode && !isOutsideAvailability) ? 0.36 : (isDragOverlay ? 1 : (isDragging ? ((activeShiftId && activeShiftId === shift.id) ? 1 : 0.9) : 1)),
  zIndex: isDragOverlay ? 1400 : 1000,
  pointerEvents: isDragOverlay ? 'none' : 'auto',
  visibility: (isDragging && activeShiftId && activeShiftId === shift.id && !isDragOverlay) ? 'hidden' : undefined,
  position: 'relative',
  };
  // use base style for the element
  const combinedStyle = baseStyle;

  const roleData = roleMap[shift.role];
  const roleColor = roleData?.color || "#888";
  const roleName = roleData?.name || shift.roleName || shift.role;

  

  // We want any unpublished shift (created as pending or explicitly not published)
  // to render with the draft/yellow appearance until it is published. Also
  // allow overlayPending to force the draft appearance while dragging.
  // Additionally, when the week is published and a shift has a `pendingUpdate`
  // we should treat it as a draft so the UI indicates the change until
  // the schedule is republished.
  const hasPendingUpdate = !!(isWeekPublished && shift.pendingUpdate && Object.keys(shift.pendingUpdate).length > 0);
  const isDraftNew = (overlayPending === true) || shift.pendingCreate === true || shift.isPublished === false || hasPendingUpdate;
  const isPendingDelete = isWeekPublished && shift.pendingDelete === true && shift.isPublished === true;
  const effective = (isWeekPublished && shift.pendingUpdate) ? { ...shift, ...shift.pendingUpdate } : shift;


  // Click detection helpers to differentiate tap vs drag. We'll use a
  // Keep click handling simple: rely on onClick and onPointerUp only.

  // Do not render deleted shifts
  if (isPendingDelete) return null;

  return (
    <Box
      ref={isDragOverlay ? null : setNodeRef}
      {...(isDragOverlay ? {} : (disabled ? {} : attributes))}
      {...(isDragOverlay ? {} : (disabled ? {} : listeners))}
      
      onClick={(e) => {
        try { e.stopPropagation(); } catch (err) {}
        // Do not open dialog if either local or parent/global dragging is active
        if (isDragOverlay || isDragging) return;
        // Prefer local openDialogImmediate if provided (bypasses menu flow)
        if (typeof localOpenDialogImmediate === 'function') {
          try {
            // Ensure any in-progress drag is cancelled before opening dialogs so
            // the dnd-kit internal state doesn't remain active and cause the
            // shift to keep following the cursor in some browsers.
            if (typeof forceEndDrag === 'function') forceEndDrag();
            if (typeof forceSuspendDrag === 'function') forceSuspendDrag(350);
          } catch (err) {}
          try { localOpenDialogImmediate(e, shift); } catch (err) {}
          try {
            // First, call any per-node listeners so the draggable sensor receives the release
            try { if (listeners && typeof listeners.onPointerUp === 'function') listeners.onPointerUp(e); } catch (err) {}
            try { if (listeners && typeof listeners.onPointerCancel === 'function') listeners.onPointerCancel(e); } catch (err) {}
            // Fallback global events
            try {
              if (typeof PointerEvent === 'function') {
                document.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, cancelable: true, button: 0 }));
                document.dispatchEvent(new PointerEvent('pointercancel', { bubbles: true, cancelable: true, button: 0 }));
              } else {
                document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, button: 0 }));
              }
            } catch (e) {}
          } catch (err) {}
          return;
        }
        if (typeof onEditClick === 'function') {
          try {
            if (typeof forceEndDrag === 'function') forceEndDrag();
            if (typeof forceSuspendDrag === 'function') forceSuspendDrag(350);
          } catch (err) {}
          try { onEditClick(e, shift); } catch (err) {}
          try {
            try { if (listeners && typeof listeners.onPointerUp === 'function') listeners.onPointerUp(e); } catch (err) {}
            try { if (listeners && typeof listeners.onPointerCancel === 'function') listeners.onPointerCancel(e); } catch (err) {}
            try {
              if (typeof PointerEvent === 'function') {
                document.dispatchEvent(new PointerEvent('pointerup', { bubbles: true, cancelable: true, button: 0 }));
                document.dispatchEvent(new PointerEvent('pointercancel', { bubbles: true, cancelable: true, button: 0 }));
              } else {
                document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true, cancelable: true, button: 0 }));
              }
            } catch (e) {}
          } catch (err) {}
        }
      }}
      onPointerUp={(e) => {
        try { e.stopPropagation(); } catch (err) {}
        if (isDragOverlay || isDragging) return;
        if (typeof localOpenDialogImmediate === 'function') {
          try { localOpenDialogImmediate(e, shift); } catch (err) {}
          return;
        }
        if (typeof onEditClick === 'function') {
          try { onEditClick(e, shift); } catch (err) {}
        }
      }}
      
      onPointerDown={(e) => {
        // Forward to dnd-kit listener if present so we don't prevent drag start
        try { if (listeners && typeof listeners.onPointerDown === 'function') listeners.onPointerDown(e); } catch (err) {}
        try {
          if (ENABLE_POINTER_CAPTURE && e && e.pointerType === 'touch' && e.currentTarget && typeof e.currentTarget.setPointerCapture === 'function') {
            try { e.currentTarget.setPointerCapture(e.pointerId); } catch (e2) { /* ignore */ }
            const release = () => {
              try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (e2) { /* ignore */ }
              try { e.currentTarget.removeEventListener('pointerup', release); } catch (e3) {}
              try { e.currentTarget.removeEventListener('pointercancel', release); } catch (e3) {}
            };
            try { e.currentTarget.addEventListener('pointerup', release); } catch (e3) {}
            try { e.currentTarget.addEventListener('pointercancel', release); } catch (e3) {}
          }
        } catch (err) {}
      }}
  onPointerMove={(e) => { try { if (listeners && typeof listeners.onPointerMove === 'function') listeners.onPointerMove(e); } catch (err) {} }}
  style={combinedStyle}
  // Only attach dnd-kit attributes/listeners to the small drag handle
      sx={{
    mb: 0.4,
    py: 0.4,
    px: 0.6,
  position: 'relative',
  zIndex: isDragOverlay ? 1400 : 1000,
        borderRadius: 1,
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.grey[800]
            : theme.palette.grey[100],
        color: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.text.primary
            : "inherit",
        borderLeft: `4px solid ${roleColor}`,
        textAlign: "left",
        boxShadow: isDragOverlay ? 3 : 1,
        cursor: 'grab',
  '&:active': { cursor: 'grabbing' },
  // edit button removed; keep hover behavior minimal
        ...(isDraftNew && {
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? '#fff59d22'
              : '#fff59d66',
          border: (theme) => `1px dashed ${theme.palette.warning.main}`,
        }),
        // Visual cue for shifts outside availability
        ...(isOutsideAvailability && !effective.isPublished && {
          bgcolor: (theme) => theme.palette.mode === 'dark' ? '#ffebee33' : '#ffebee88',
          border: (theme) => `2px solid ${theme.palette.error.main}`,
        }),
        // If conflictMode is active and this shift is NOT outside availability,
        // dim it so actual conflicts stand out.
        ...(conflictMode && !isOutsideAvailability && {
          opacity: 0.36,
          filter: 'grayscale(80%)',
        }),
        ...(isOutsideAvailability && effective.isPublished && {
          boxShadow: 'none',
          border: (theme) => `2px solid ${theme.palette.error.main}`,
        }),
      }}
    >
  {/* edit icon removed; clicking the shift still opens the edit dialog via onClick/onPointerUp handlers */}
    {/* dnd-kit attributes/listeners attached to the full Box so
      users can click to edit or grab anywhere to start a drag. */}
  {/* pointer-capture debug UI removed */}
  <Typography
        variant="body2"
        sx={{ fontWeight: 600 }}
        onPointerDown={(e) => {
          try {
            if (ENABLE_POINTER_CAPTURE && e && e.pointerType === 'touch' && e.currentTarget && typeof e.currentTarget.setPointerCapture === 'function') {
              try { e.currentTarget.setPointerCapture(e.pointerId); } catch (e2) { /* ignore */ }
              const release = () => {
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (e2) { /* ignore */ }
                try { e.currentTarget.removeEventListener('pointerup', release); } catch (e3) {}
                try { e.currentTarget.removeEventListener('pointercancel', release); } catch (e3) {}
              };
              try { e.currentTarget.addEventListener('pointerup', release); } catch (e3) {}
              try { e.currentTarget.addEventListener('pointercancel', release); } catch (e3) {}
            }
          } catch (err) {}
        }}
      >
        {formatTime(effective.shiftStartTime)} – {formatTime(effective.shiftEndTime)}
      </Typography>
  <Typography
        variant="caption"
        color="text.secondary"
        onPointerDown={(e) => {
          try {
            if (ENABLE_POINTER_CAPTURE && e && e.pointerType === 'touch' && e.currentTarget && typeof e.currentTarget.setPointerCapture === 'function') {
              try { e.currentTarget.setPointerCapture(e.pointerId); } catch (e2) { /* ignore */ }
              const release = () => {
                try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (e2) { /* ignore */ }
                try { e.currentTarget.removeEventListener('pointerup', release); } catch (e3) {}
                try { e.currentTarget.removeEventListener('pointercancel', release); } catch (e3) {}
              };
              try { e.currentTarget.addEventListener('pointerup', release); } catch (e3) {}
              try { e.currentTarget.addEventListener('pointercancel', release); } catch (e3) {}
            }
          } catch (err) {}
        }}
      >
        {roleName}
      </Typography>
      
      {effective.notes && (
        <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary', mt: 0.5 }}>
          "{effective.notes}"
        </Typography>
      )}
      {effective.isOpenShift && (
        <Typography variant="caption" color="primary" sx={{ display: 'block', fontStyle: 'italic' }}>
          Open Shift
        </Typography>
      )}
    </Box>
  );
}

// Visual-only ghost of a shift: renders the same times/role but is non-draggable
// and intentionally doesn't register with dnd-kit. Use this to show a shift
// in other role rows without creating a second interactive/drag target.
// Ghost shifts removed per user request: show only interactive shifts.

// Droppable Cell Component
function DroppableCell({ id, children, onContextMenu, openCellMenu, openDialogImmediate, isDragging = false, forceOver = false, ...props }) {
  const { isOver, setNodeRef } = useDroppable({ id });
  const effectiveOver = Boolean(isOver || (forceOver && isDragging));
  const [hover, setHover] = useState(false);

  const handlePlusClick = (e) => {
    e.stopPropagation();
    try { e.preventDefault(); } catch (err) {}
    // Find bounding rect of the droppable cell to compute reasonable coordinates for the menu
    const cellEl = e.currentTarget ? e.currentTarget.closest('[data-droppable-id]') : null;
    const rect = cellEl ? cellEl.getBoundingClientRect() : { left: e.clientX || 0, top: e.clientY || 0, width: 0, height: 0 };
    const fakeEvent = {
      clientX: rect.left + (rect.width / 2),
      clientY: rect.top + (rect.height / 2),
      preventDefault: () => {},
      stopPropagation: () => {},
  // mark as non-primary click so parent's openMenu won't auto-open edit dialog
  button: 2,
  currentTarget: e.currentTarget,
  source: 'plus',
  __source: 'plus',
    };
    // If the parent provided an openDialogImmediate handler, open the Add Shift dialog directly.
    if (typeof openDialogImmediate === 'function') {
      try { openDialogImmediate(fakeEvent); } catch (err) { /* fall back */ }
      return;
    }
    // Prefer explicit openCellMenu (bound to openMenu in parent). Fall back to onContextMenu if provided.
    if (openCellMenu) openCellMenu(fakeEvent);
    else if (onContextMenu) onContextMenu(fakeEvent);
  };

  return (
    <TableCell
      ref={setNodeRef}
      data-droppable-id={id}
      onContextMenu={onContextMenu}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...props}
      sx={{
        ...props.sx,
        // Subtle blue fill when hovered; no outline or shadow — user requested
        // a soft fill only so the UI is cleaner.
        bgcolor: effectiveOver ? (theme) => alpha(theme.palette.primary.main, 0.06) : props.sx?.bgcolor,
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        transition: 'background-color 0.12s ease-in-out',
        position: 'relative',
      }}
    >
      {children}
      {!isDragging && hover && (
        <IconButton
          size="small"
          onClick={handlePlusClick}
          aria-label="Add shift"
          sx={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95), '&:hover': { bgcolor: (theme) => alpha(theme.palette.background.paper, 1) }, boxShadow: 1, zIndex: 1500, pointerEvents: 'auto' }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      )}
    </TableCell>
  );
}

export default function ScheduleGridWithDnD({
  byDept,
  // some callers use the name `byDeptHierarchy` (other file `ScheduleGrid.js` uses that name).
  // Accept either prop name for compatibility.
  byDeptHierarchy,
  weekDays,
  shifts,
  empIdSet,
  analytics,
  openMenu,
  roles,
  openDialogImmediate,
  openMenuImmediate,
  onDayHeaderClick,
  deptNameMap,
  onShiftMove,
  selectedLocation,
  disabled = false,
  isWeekPublished = false,
  headerOffset = 0,
  conflictMode = false,
}) {
  const getContainerHeight = React.useCallback(() => {
    try {
      const headerH = Number(headerOffset) || 64;
      return Math.max(300, window.innerHeight - headerH - 120);
    } catch (e) {
      return 600;
    }
  }, [headerOffset]);

  const [footerMode, setFooterMode] = useState('percent'); // 'percent' | 'dollars'
  // Resolve department source from either prop so the component works with both callers.
  const deptSource = useMemo(() => (byDept || byDeptHierarchy || {}), [byDept, byDeptHierarchy]);
  const [suspendDrag, setSuspendDrag] = React.useState(false);
  const [dndContextKey, setDndContextKey] = React.useState(0);
  // If suspendDrag is true, use a very large activation distance so drag
  // never begins. Recreate sensors when suspendDrag changes.
  // Use a short delay-based activationConstraint so quick clicks/taps
  // are treated as clicks while allowing grab-and-hold to start a drag.
  const sensorsActive = useSensors(
    useSensor(PointerSensor, { activationConstraint: disabled ? { distance: 999999 } : (suspendDrag ? { distance: 999999 } : { delay: 160, tolerance: 5 }) })
  );

  // Load availability for current location so shifts can be validated visually
  const { availability } = useAvailability(selectedLocation);
  // Load per-location assignments map: employeeId -> nested assignments for selectedLocation
  const { map: assignmentsMap } = useAssignmentsForLocation(selectedLocation);

  const forceSuspendDrag = React.useCallback((ms = 300) => {
    try { setSuspendDrag(true); } catch (e) {}
    try { setTimeout(() => { setSuspendDrag(false); }, ms); } catch (e) {}
  }, []);

  const [activeShift, setActiveShift] = useState(null);
  const [optimisticMoves, setOptimisticMoves] = useState(new Map());
  const [isDragging, setIsDragging] = useState(false);
  // Quick view state for employee details
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewEmployee, setQuickViewEmployee] = useState(null);
  const lastPointerRef = React.useRef({ x: null, y: null });
  const lastOverRef = React.useRef(null);
  const [currentOverId, setCurrentOverId] = useState(null);

  // Compute container height so the schedule grid fills available viewport
  // space beneath the app header. The parent can provide `headerOffset` (px)
  // to account for fixed headers; fall back to 64px if not provided.
  const [containerHeight, setContainerHeight] = useState(() => getContainerHeight());

  useEffect(() => {
    const handleResize = () => {
      const nextHeight = getContainerHeight();
      setContainerHeight(prev => (prev === nextHeight ? prev : nextHeight));
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      try { window.removeEventListener('resize', handleResize); } catch (e) {}
    };
  }, [getContainerHeight]);
  // If the parent removes or deletes a shift, ensure any optimistic entry for it is also removed
  useEffect(() => {
    if (!optimisticMoves || optimisticMoves.size === 0) return;
    const existingIds = new Set(shifts.map(s => s.id));
    let changed = false;
    const newMap = new Map(optimisticMoves);
    for (const id of Array.from(newMap.keys())) {
      if (!existingIds.has(id)) { newMap.delete(id); changed = true; }
    }
    if (changed) {
  // cleaned up optimisticMoves for deleted shifts
      setOptimisticMoves(newMap);
    }
  }, [shifts, optimisticMoves]);

  // Create roleMap for quick lookup: { roleId: { name, color } }
  const roleMap = useMemo(() => {
    const map = {};
    roles.forEach(r => { map[r.id] = { name: r.name, color: r.color || "#888", archived: !!r.archived }; });
    map["unassigned"] = { name: "Unassigned", color: "#666", archived: false };
    return map;
  }, [roles]);

  // Load locations so we can map locationId -> friendly name for ghost captions
  const locations = useLocations(selectedLocation, () => {});
  const locationMap = useMemo(() => {
    const m = {};
    (locations || []).forEach(l => { if (l && l.id) m[l.id] = l; });
    return m;
  }, [locations]);

  // Effective shifts: when published, preview pendingUpdate values; hide truly deleted; de-duplicate by ID
  // Remove deleted shifts from the grid, but keep them in unpublished changes
  const effectiveShifts = useMemo(() => {
    const byId = new Map();
    shifts.forEach((s) => {
  // Hide shifts that are deleted or pending deletion immediately in the UI
  if (s.deleted || s.pendingDelete === true) return;
      const eff = (isWeekPublished && s.pendingUpdate && Object.keys(s.pendingUpdate || {}).length > 0)
        ? { ...s, ...s.pendingUpdate }
        : s;
      byId.set(s.id, eff);
    });
    // apply optimistic moves only when not actively dragging to avoid
    // rendering a duplicate (ghost) while the user is dragging.
    if (!isDragging) {
      optimisticMoves.forEach((optimisticShift, shiftId) => {
        byId.set(shiftId, optimisticShift);
      });
    }
    return Array.from(byId.values());
  }, [shifts, isWeekPublished, isDragging, optimisticMoves]);

  // Separate open shifts from assigned shifts using effective shifts
  const { openShifts, assignedShifts } = useMemo(() => {
    const open = effectiveShifts.filter(s => s.isOpenShift);
    const assigned = effectiveShifts.filter(s => !s.isOpenShift);
    return { openShifts: open, assignedShifts: assigned };
  }, [effectiveShifts]);

  // Staffing targets: load per-location staffingTargets doc from Firestore (if present)
  const [staffingTargetsDoc, setStaffingTargetsDoc] = useState(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!selectedLocation) { setStaffingTargetsDoc(null); return; }
      try {
        const tenantId = await tryResolveTenantId();
        if (!tenantId) { setStaffingTargetsDoc(null); return; }
        const ref = doc(db, `tenants/${tenantId}/staffingTargets`, selectedLocation);
        const snap = await getDoc(ref);
        if (!mounted) return;
        if (snap && snap.exists()) setStaffingTargetsDoc(snap.data());
        else setStaffingTargetsDoc(null);
      } catch (err) {
        console.error('Failed to load staffingTargets doc', err);
        if (mounted) setStaffingTargetsDoc(null);
      }
    })();
    return () => { mounted = false; };
  }, [selectedLocation]);

  // Compute heatmap data for weekDays: aggregate scheduled headcount per daypart
  const staffingHeat = useMemo(() => {
    if (!Array.isArray(weekDays) || weekDays.length === 0) return {};
    // Prepare daypart definitions
    const dayparts = (staffingTargetsDoc && staffingTargetsDoc.dayparts) || [];
    // Build targets map: dayKey -> daypartId -> roleId? -> target
    const targets = (staffingTargetsDoc && staffingTargetsDoc.targets) || {};

    // Helper to find which daypart a minute falls into (handles crossing-midnight by mapping into 0..1439)
    const findDaypartForMinute = (minute) => {
      for (let dp of dayparts) {
        try {
          const s = dp.start ? dp.start : '00:00';
          const e = dp.end ? dp.end : '23:59';
          const sM = (() => { const [hh, mm] = s.split(':').map(Number); return hh * 60 + mm; })();
          let eM = (() => { const [hh, mm] = e.split(':').map(Number); return hh * 60 + mm; })();
          // treat end === 0 as 24:00
          if (eM === 0) eM = 24 * 60;
          // crossing midnight
          if (eM <= sM) {
            if (minute >= sM || minute < eM) return dp.id;
          } else {
            if (minute >= sM && minute < eM) return dp.id;
          }
        } catch (err) { continue; }
      }
      return null;
    };

    const out = {};
  weekDays.forEach(d => {
	const key = toIsoDateString(d);
      out[key] = { dayparts: {}, totals: { target: 0, scheduled: 0 } };
      // initialize dayparts
      (dayparts || []).forEach(dp => { out[key].dayparts[dp.id] = { id: dp.id, name: dp.name, target: 0, scheduled: 0 }; });
    });

    // Build per-day targets using 'ALL' as base and overriding with day-specific entries
    const baseAll = targets['ALL'] || targets['All'] || targets['all'] || {};
    const sumRoleMap = (roleMap) => {
      if (!roleMap) return 0;
      return Object.values(roleMap).reduce((acc, v) => {
        if (v == null) return acc;
        if (typeof v === 'number') return acc + v;
        if (typeof v === 'string') {
          const n = Number(v);
          if (!isNaN(n)) return acc + n;
          // non-numeric string likely an id -> count as 1
          return acc + 1;
        }
        if (typeof v === 'object') {
          const vals = Object.values(v || {});
          // if nested values are numeric-like, sum them; otherwise count keys
          if (vals.length > 0 && vals.every(x => typeof x === 'number' || (!isNaN(Number(x)) && x !== null && x !== undefined))) {
            return acc + vals.reduce((a, b) => a + (Number(b) || 0), 0);
          }
          return acc + Object.keys(v || {}).length;
        }
        // fallback: count as 1
        return acc + 1;
      }, 0);
    };
  weekDays.forEach(d => {
	const k = toIsoDateString(d);
      // start with baseAll values
      Object.keys(baseAll || {}).forEach(dpId => {
        const roleMap = baseAll[dpId] || {};
        const totalForDp = sumRoleMap(roleMap);
        if (!out[k].dayparts[dpId]) out[k].dayparts[dpId] = { id: dpId, name: dpId, target: 0, scheduled: 0 };
        out[k].dayparts[dpId].target = totalForDp;
      });

      // apply day-specific override if present (weekday short name or ISO date key)
      const wkShort = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dayEntry = targets[wkShort] || targets[k] || {};
      Object.keys(dayEntry || {}).forEach(dpId => {
        const roleMap = dayEntry[dpId] || {};
        const totalForDp = sumRoleMap(roleMap);
        if (!out[k].dayparts[dpId]) out[k].dayparts[dpId] = { id: dpId, name: dpId, target: 0, scheduled: 0 };
        out[k].dayparts[dpId].target = totalForDp;
      });

      // compute totals from final daypart values
      out[k].totals.target = Object.values(out[k].dayparts || {}).reduce((sum, p) => sum + (Number(p.target) || 0), 0);
    });

    // Aggregate scheduled employees: consider assignedShifts (non-open) and count unique employees per daypart
    const seenSet = new Set();
    (assignedShifts || []).forEach(s => {
      try {
	const sd = s.shiftDateKey ? toDate(s.shiftDateKey) : toDate(s.shiftDate);
	const key = toIsoDateString(sd);
        if (!out[key]) return;
        const st = (s.shiftStartTime || s.startTime || '00:00');
        const en = (s.shiftEndTime || s.endTime || '00:00');
        const stM = (() => { const [hh, mm] = st.split(':').map(Number); return hh * 60 + (mm || 0); })();
        let enM = (() => { const [hh, mm] = en.split(':').map(Number); return hh * 60 + (mm || 0); })();
        if (enM === 0) enM = 24 * 60;
        // split shift into 15-minute buckets and mark which dayparts this employee covers
        for (let m = stM; m < enM; m += 15) {
          const dpId = findDaypartForMinute(m);
          if (!dpId) continue;
          if (!out[key].dayparts[dpId]) out[key].dayparts[dpId] = { id: dpId, name: dpId, target: 0, scheduled: 0 };
          // ensure we count unique employee per daypart only once
          const uid = `${s.employeeId}::${dpId}::${key}`;
          if (!seenSet.has(uid)) {
            seenSet.add(uid);
            out[key].dayparts[dpId].scheduled += 1;
            out[key].totals.scheduled += 1;
          }
        }
      } catch (err) { /* ignore parse errors */ }
    });

    return out;
  }, [weekDays, staffingTargetsDoc, assignedShifts, effectiveShifts, optimisticMoves]);

  

  // Group employees by department and role
  const employeesByDeptAndRole = useMemo(() => {
    const structure = {};

    // Debug: log a concise summary of deptSource/roles/selectedLocation to diagnose missing employees
    try {
      const deptKeys = deptSource ? (Array.isArray(deptSource) ? deptSource.length : Object.keys(deptSource).length) : 0;
  // building employeesByDeptAndRole
      // Log a small sample of the first dept entry keys/types to help debug shapes
      if (deptKeys > 0) {
        const sampleKey = Array.isArray(deptSource) ? 0 : Object.keys(deptSource)[0];
        const sampleVal = deptSource ? deptSource[sampleKey] : undefined;
  // dept sample logged for debugging previously
      }
  } catch (err) { /* ignore debug failure */ }

    // If no departments, return empty structure
    if (!deptSource || Object.keys(deptSource).length === 0) {
      return structure;
    }
    
    // If no roles, create fallback structure with all employees
    if (!roles || roles.length === 0) {
      Object.entries(deptSource).forEach(([dept, emps]) => {
        const deptEmps = Array.isArray(emps) ? emps : (emps ? Object.values(emps) : []);
        if (deptEmps && deptEmps.length > 0) {
          structure[dept] = {
            "unassigned": deptEmps.sort((a, b) => {
              const nameA = a.name || [a.firstName, a.lastName].filter(Boolean).join(" ");
              const nameB = b.name || [b.firstName, b.lastName].filter(Boolean).join(" ");
              return nameA.localeCompare(nameB);
            })
          };
        }
      });
      return structure;
    }

  Object.entries(deptSource).forEach(([dept, emps]) => {
      if (!structure[dept]) structure[dept] = {};

      // If the incoming deptSource is already hierarchical (roleId -> Employee[]),
      // treat it as authoritative and copy entries directly. This happens when the
      // parent component passes `byDeptHierarchy` rather than the flat `byDept` map.
      if (emps && typeof emps === 'object') {
        const vals = Object.values(emps);
        if (vals.length > 0 && Array.isArray(vals[0])) {
          // Copy each role array into the structure and preserve ordering later
          Object.entries(emps).forEach(([roleId, roleArr]) => {
            structure[dept][roleId] = (roleArr || []).slice().sort((a, b) => {
              const nameA = (a && (a.name || [a.firstName, a.lastName].filter(Boolean).join(" "))) || '';
              const nameB = (b && (b.name || [b.firstName, b.lastName].filter(Boolean).join(" "))) || '';
              return nameA.localeCompare(nameB);
            });
          });
          // We've consumed this dept source; skip the rest of the assembly logic.
          return;
        }
      }

      // Get all roles used in this department (flat deptSource case)
      const deptRoles = new Set();
      let deptEmps = [];
      if (Array.isArray(emps)) {
        deptEmps = emps;
      } else if (emps && typeof emps === 'object') {
        const vals = Object.values(emps);
        // If values are arrays (roleId -> Employee[]), flatten them - this path
        // is unlikely now because we handled that case above, but keep for safety.
        if (vals.length > 0 && Array.isArray(vals[0])) {
          deptEmps = vals.reduce((acc, v) => acc.concat(v || []), []);
        } else {
          // Otherwise assume it's id->employee map
          deptEmps = vals;
        }
      }
      
  deptEmps.forEach(emp => {
    // Prefer the per-location assignments map when available (flattened canonical source)
    const empAssignmentsSlice = (assignmentsMap && assignmentsMap[emp.id]) || null;
    // Use the selectedLocation parameter first
    if (selectedLocation && empAssignmentsSlice) {
      const locAssignments = empAssignmentsSlice; // already scoped to selectedLocation

      if (locAssignments.departments && locAssignments.departments[dept]) {
        const deptAssignment = locAssignments.departments[dept];

        if (Array.isArray(deptAssignment.roles)) {
          deptAssignment.roles.forEach(roleId => { deptRoles.add(roleId); });
        } else if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
          Object.entries(deptAssignment.roles).forEach(([roleId, meta]) => {
            if (!meta) return;
            if (meta.assigned || meta.isPrimary) deptRoles.add(roleId);
          });
        }
      }
    } else if (selectedLocation && ((assignmentsMap && assignmentsMap[emp.id]) || (emp.assignments && emp.assignments[selectedLocation]))) {
      // Prefer flattened assignments when available; fall back to legacy nested shape
      const locAssignments = (assignmentsMap && assignmentsMap[emp.id]) || emp.assignments[selectedLocation];

      if (locAssignments.departments && locAssignments.departments[dept]) {
        const deptAssignment = locAssignments.departments[dept];

        if (Array.isArray(deptAssignment.roles)) {
          deptAssignment.roles.forEach(roleId => { deptRoles.add(roleId); });
        } else if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
          Object.entries(deptAssignment.roles).forEach(([roleId, meta]) => {
            if (!meta) return;
            if (meta.assigned || meta.isPrimary) deptRoles.add(roleId);
          });
        }
      }
    } else {
      // Try to find any location ID as fallback (prefer assignmentsMap first)
      if (empAssignmentsSlice) {
        const locAssignments = empAssignmentsSlice;
        if (locAssignments.departments && locAssignments.departments[dept]) {
          const deptAssignment = locAssignments.departments[dept];
          if (Array.isArray(deptAssignment.roles)) {
            deptAssignment.roles.forEach(roleId => { deptRoles.add(roleId); });
          } else if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
            Object.entries(deptAssignment.roles).forEach(([roleId, meta]) => {
              if (!meta) return;
              if (meta.assigned || meta.isPrimary) deptRoles.add(roleId);
            });
          }
        }
      } else {
        const locationIds = emp.assignments ? Object.keys(emp.assignments) : [];

        if (locationIds.length > 0) {
          // Use the first available location ID
          const locId = locationIds[0];
          const locAssignments = emp.assignments[locId];

          if (locAssignments.departments && locAssignments.departments[dept]) {
            const deptAssignment = locAssignments.departments[dept];

            if (Array.isArray(deptAssignment.roles)) {
              deptAssignment.roles.forEach(roleId => { deptRoles.add(roleId); });
            } else if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
              Object.entries(deptAssignment.roles).forEach(([roleId, meta]) => {
                if (!meta) return;
                if (meta.assigned || meta.isPrimary) deptRoles.add(roleId);
              });
            }
          }
        }
      }
    }
  });
      
      // If no roles found for this department, create a fallback
      if (deptRoles.size === 0) {
        deptRoles.add("unassigned");
      }
      
      // Organize employees by role
      // Make role ordering deterministic by respecting the provided `roles` prop order when possible
      const orderedDeptRoles = Array.from(deptRoles);
      if (roles && roles.length > 0) {
        orderedDeptRoles.sort((a, b) => {
          const ai = roles.findIndex(r => r.id === a);
          const bi = roles.findIndex(r => r.id === b);
          if (ai === -1 && bi === -1) return String(a).localeCompare(String(b));
          if (ai === -1) return 1;
          if (bi === -1) return -1;
          return ai - bi;
        });
      }
      orderedDeptRoles.forEach(roleId => {
        const roleEmployees = deptEmps.filter(emp => {
          if (roleId === "unassigned") return true;

          const empAssignmentsSlice = (assignmentsMap && assignmentsMap[emp.id]) || null;

          if (selectedLocation && empAssignmentsSlice) {
            const locAssignments = empAssignmentsSlice;
            if (locAssignments.departments && locAssignments.departments[dept]) {
              const deptAssignment = locAssignments.departments[dept];
              if (Array.isArray(deptAssignment.roles)) return deptAssignment.roles.includes(roleId);
              if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
                const meta = deptAssignment.roles[roleId];
                return !!(meta && (meta.assigned || meta.isPrimary));
              }
              return false;
            }
          } else if (selectedLocation && ((assignmentsMap && assignmentsMap[emp.id]) || (emp.assignments && emp.assignments[selectedLocation]))) {
            const locAssignments = (assignmentsMap && assignmentsMap[emp.id]) || emp.assignments[selectedLocation];
            if (locAssignments.departments && locAssignments.departments[dept]) {
              const deptAssignment = locAssignments.departments[dept];
              if (Array.isArray(deptAssignment.roles)) return deptAssignment.roles.includes(roleId);
              if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
                const meta = deptAssignment.roles[roleId];
                return !!(meta && (meta.assigned || meta.isPrimary));
              }
              return false;
            }
          } else {
            if (empAssignmentsSlice) {
              const locAssignments = empAssignmentsSlice;
              if (locAssignments.departments && locAssignments.departments[dept]) {
                const deptAssignment = locAssignments.departments[dept];
                if (Array.isArray(deptAssignment.roles)) return deptAssignment.roles.includes(roleId);
                if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
                  const meta = deptAssignment.roles[roleId];
                  return !!(meta && (meta.assigned || meta.isPrimary));
                }
                return false;
              }
            } else {
              const locationIds = emp.assignments ? Object.keys(emp.assignments) : [];
              if (locationIds.length > 0) {
                const locId = locationIds[0];
                const locAssignments = emp.assignments[locId];
                if (locAssignments.departments && locAssignments.departments[dept]) {
                  const deptAssignment = locAssignments.departments[dept];
                  if (Array.isArray(deptAssignment.roles)) return deptAssignment.roles.includes(roleId);
                  if (deptAssignment.roles && typeof deptAssignment.roles === 'object') {
                    const meta = deptAssignment.roles[roleId];
                    return !!(meta && (meta.assigned || meta.isPrimary));
                  }
                  return false;
                }
              }
            }
          }
          return false;
        });
        
        // Always include the role in the structure even if there are no employees
        // for this week so departments/roles appear on empty future weeks.
        structure[dept][roleId] = (roleEmployees || []).slice().sort((a, b) => {
          const nameA = (a && (a.name || [a.firstName, a.lastName].filter(Boolean).join(" "))) || '';
          const nameB = (b && (b.name || [b.firstName, b.lastName].filter(Boolean).join(" "))) || '';
          return nameA.localeCompare(nameB);
        });
      });
    });
    
    try {
      const summary = Object.keys(structure).map(d => ({ dept: d, roles: Object.keys(structure[d] || {}).map(r => ({ role: r, count: (structure[d][r] || []).length })) }));
  // employeesByDeptAndRole summary built
    } catch (err) { /* ignore */ }
    return structure;
  }, [deptSource, roles, selectedLocation]);

  // Local debug: print deptSource keys, roleMap contents and final structure
  // debug instrumentation removed

  // Group open shifts by department and role
  const openShiftsByDeptAndRole = useMemo(() => {
    const map = {};
    openShifts.forEach(shift => {
      if (!map[shift.dept]) map[shift.dept] = {};
      if (!map[shift.dept][shift.role]) map[shift.dept][shift.role] = [];
      map[shift.dept][shift.role].push(shift);
    });
    return map;
  }, [openShifts]);

  // allShiftIds removed — using primitive draggable/droppable so droppable ids
  // correspond to cells rather than other shift ids.

  const handleDragStart = (event) => {
    if (disabled) return;
    const { active } = event;
    // Use effective shifts for nicer overlay content if pendingUpdate exists
    setActiveShift(effectiveShifts.find(s => s.id === active.id));
  setIsDragging(true);
  try { if (document && document.body && document.body.classList) document.body.classList.add('dnd-dragging'); } catch (e) {}
  // track pointer coordinates during drag so we can fallback to elementFromPoint
  const pointerMoveHandler = (e) => { lastPointerRef.current = { x: e.clientX, y: e.clientY }; };
  try { document.addEventListener('pointermove', pointerMoveHandler); event._pointerMoveHandler = pointerMoveHandler; } catch (e) {}
  // drag started for active id
  };


  // Forcefully end any in-progress drag from child components. This is
  // used by DraggableShift when a click opens the Edit dialog and we need
  // to ensure the drag state is cleared so the shift doesn't keep moving.
  const forceEndDrag = React.useCallback(() => {
    setActiveShift(null);
    setIsDragging(false);
    try { setCurrentOverId(null); } catch (e) {}
    try { if (document && document.body && document.body.classList) document.body.classList.remove('dnd-dragging'); } catch (e) {}
  try { setDndContextKey(k => (k || 0) + 1); } catch (e) {}
  }, [setActiveShift, setIsDragging]);

  const handleDragOver = (event) => {
    // Track the last droppable the pointer was over — dnd-kit sometimes
    // yields `over`=undefined on drag end even though it had an `over`
    // earlier. Remembering the last one increases robustness. Also
    // keep a lightweight `currentOverId` state so we can force the
    // droppable highlight visually while dragging if necessary.
    try {
      lastOverRef.current = event.over;
      const overId = event.over?.id || null;
      setCurrentOverId(overId);
      // Lightweight debug to confirm dnd-kit is reporting the droppable under pointer
  // handleDragOver overId: overId
    } catch (e) { lastOverRef.current = null; setCurrentOverId(null); }
  };

  const handleDragEnd = (event) => {
    if (disabled) return;
    const { active, over } = event;
    setActiveShift(null);
    setIsDragging(false);
  setCurrentOverId(null);
  try { if (document && document.body && document.body.classList) document.body.classList.remove('dnd-dragging'); } catch (e) {}
  // remove pointermove listener
  try { if (event && event._pointerMoveHandler) { document.removeEventListener('pointermove', event._pointerMoveHandler); } } catch (e) {}
  // drag ended

  // If dnd-kit couldn't resolve a droppable, try a robust fallback using
  // (a) the last over reported during the drag, then (b) the last pointer coordinates
  let resolvedOver = over || lastOverRef.current;
    if (!resolvedOver) {
      try {
        const p = lastPointerRef.current;
        if (p && p.x != null && p.y != null) {
          const el = document.elementFromPoint(p.x, p.y);
          const found = el ? el.closest('[data-droppable-id]') : null;
          if (found && found.dataset && found.dataset.droppableId) {
            resolvedOver = { id: found.dataset.droppableId };
            // fallback resolved over via elementFromPoint
          }
        }
  } catch (e) { /* ignore fallback failure */ }
    }

    if (!resolvedOver) return;

    const activeShift = shifts.find(s => s.id === active.id);
    if (!activeShift) return;

  const { type: targetType, parts: targetParts } = parseDroppableId(resolvedOver.id);
    
    if (targetType === 'employee') {
      const [empId, dateStr, dept, roleId] = targetParts;
      const targetDate = weekDays.find(d => toIsoDateString(d) === dateStr);
      if (!targetDate) { console.error("Could not find matching date in weekDays for:", dateStr); return; }
      // optimistic update so UI reflects the move immediately
  // Mark optimistic move as a draft so it renders with the unpublished (yellow) style immediately
  const optimisticShift = { ...activeShift, employeeId: empId, shiftDate: targetDate, shiftDateKey: toIsoDateString(targetDate), dept, role: roleId, isOptimistic: true, isPublished: false, pendingCreate: true };
  // optimistic add applied
  setOptimisticMoves(prev => new Map(prev).set(activeShift.id, optimisticShift));
      onShiftMove?.(activeShift, { employeeId: empId, date: targetDate, dept: dept, role: roleId, isOpenShift: false, })
        .then(() => {
          setTimeout(() => setOptimisticMoves(prev => { const m = new Map(prev); m.delete(activeShift.id); return m; }), 150);
        })
        .catch(() => { setOptimisticMoves(prev => { const m = new Map(prev); m.delete(activeShift.id); return m; }); });
    } else if (targetType === 'openshift') {
      const [dept, roleId, dateStr] = targetParts;
      const targetDate = weekDays.find(d => toIsoDateString(d) === dateStr);
      if (!targetDate) { console.error("Could not find matching date in weekDays for:", dateStr); return; }
  // Mark optimistic move to open shift as a draft so it renders unpublished immediately
  const optimisticShift = { ...activeShift, employeeId: null, shiftDate: targetDate, shiftDateKey: toIsoDateString(targetDate), dept, role: roleId, isOptimistic: true, isPublished: false, pendingCreate: true };
  // optimistic openshift add applied
  setOptimisticMoves(prev => new Map(prev).set(activeShift.id, optimisticShift));
      onShiftMove?.(activeShift, { employeeId: null, date: targetDate, dept: dept, role: roleId, isOpenShift: true, })
        .then(() => {
          setTimeout(() => setOptimisticMoves(prev => { const m = new Map(prev); m.delete(activeShift.id); return m; }), 150);
        })
        .catch(() => { setOptimisticMoves(prev => { const m = new Map(prev); m.delete(activeShift.id); return m; }); });
    }
  };

  const getDroppableId = (type, ...parts) => `${type}||${parts.join('||')}`;
  const parseDroppableId = (id) => { const [type, ...parts] = id.split('||'); return { type, parts }; };

  return (
  <DndContext
      key={dndContextKey}
      sensors={sensorsActive}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
  <TableContainer component={Paper} sx={{ border: 1, borderColor: "grey.300", maxHeight: containerHeight, height: containerHeight, overflow: 'auto', p: 0 }}>
          {/* Footer mode selector removed from top-right — control moved into footer row */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderRight: (theme) => `1px solid ${theme.palette.divider}`, position: 'sticky', top: '0px', zIndex: 1200, bgcolor: (theme) => theme.palette.background.paper }} />
                {weekDays.map((d, i) => {
                  const dayKey = toIsoDateString(d);
                  const scheduledEmpIds = new Set();
                  assignedShifts.forEach(s => {
                    try {
                      const { localDate: key } = extractShiftLocalTime(s);
                      if (key === dayKey && s.employeeId) scheduledEmpIds.add(s.employeeId);
                    } catch (err) { /* ignore parsing errors */ }
                  });
                  const dayCount = scheduledEmpIds.size;
                  return (
                    <TableCell
                      data-day-index={i}
                      key={toIsoDateString(d)}
                      align="left"
                      onClick={() => { try { if (typeof onDayHeaderClick === 'function') onDayHeaderClick(d); } catch (err) {} }}
                      sx={{ position: 'sticky', top: '0px', zIndex: 1199, bgcolor: (theme) => theme.palette.background.paper, minWidth: 140, borderLeft: '1px solid rgba(0,0,0,0.06)', borderBottom: (theme) => `1px solid ${theme.palette.divider}`, borderRight: (theme) => `1px solid ${theme.palette.divider}`, px: 2, py: 1, cursor: 'pointer' }}
                    >
                      <Box sx={{ position: 'relative', minHeight: 52 }}>
                        <Box sx={{ position: 'absolute', top: 6, right: 8, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                          <GroupIcon fontSize="small" />
                          <Typography variant="caption" color="text.secondary">{dayCount}</Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{d.toLocaleDateString(undefined, { weekday: 'short' })}</Typography>
                        <Typography variant="caption" color="text.secondary">{d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</Typography>

                        {/* heatmap row moved below headers */}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>

              {/* Thin heatmap row under the date headers */}
              <TableRow>
                <TableCell sx={{ borderRight: (theme) => `1px solid ${theme.palette.divider}`, height: 12 }} />
                {weekDays.map((d, i) => {
                  const key = toIsoDateString(d);
                  const data = staffingHeat && staffingHeat[key] ? staffingHeat[key] : null;
                  if (!data) {
                    return <TableCell key={`heat-empty-${key}`} sx={{ minWidth: 140, borderLeft: '1px solid rgba(0,0,0,0.06)', borderRight: (theme) => `1px solid ${theme.palette.divider}`, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, px: 0, py: 0 }} />;
                  }
                  const parts = Object.values(data.dayparts || {});
                  const totalTarget = data.totals?.target || 0;
                  const totalScheduled = data.totals?.scheduled || 0;
                  const tipLines = parts.map(p => {
                    const diff = (p.scheduled || 0) - (p.target || 0);
                    const status = diff < 0 ? `under by ${Math.abs(diff)}` : (diff > 0 ? `over by ${diff}` : 'on target');
                    return `${p.name}: target ${p.target || 0}, scheduled ${p.scheduled || 0} (${status})`;
                  });
                  // Prefer showing understaffing if any daypart is short, even if another is over
                  const totalShort = parts.reduce((s, p) => s + Math.max(0, (p.target || 0) - (p.scheduled || 0)), 0);
                  const totalOver = parts.reduce((s, p) => s + Math.max(0, (p.scheduled || 0) - (p.target || 0)), 0);
                  const overallStatus = totalShort > 0 ? `Under by ${totalShort}` : (totalOver > 0 ? `Over by ${totalOver}` : 'On target');
                  const colorFor = (scheduled, target) => {
                    const diff = (scheduled || 0) - (target || 0);
                    if (diff < 0) {
                      const mag = Math.min(1, Math.abs(diff) / Math.max(1, target || 1));
                      return mag > 0.6 ? '#ff6b6b' : (mag > 0.3 ? '#ff8a65' : '#ffb74d');
                    }
                    if (diff === 0) return '#3bb273';
                    // Overstaffed: use requested blue shade (uniform)
                    return '#1f78de';
                  };

                  return (
                    <TableCell key={`heat-${key}`} sx={{ minWidth: 140, borderLeft: '1px solid rgba(0,0,0,0.06)', borderRight: (theme) => `1px solid ${theme.palette.divider}`, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, px: 1, py: 0 }}>
                      <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}><div style={{ fontWeight: 700, marginBottom: 6 }}>{overallStatus}</div><div>{tipLines.join('\n')}</div></div>} arrow>
                        <Box sx={{ height: 8, display: 'flex', borderRadius: 1, overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.02)' }}>
                          {parts.length > 0 ? (() => {
                            // compute a weight for each part so parts with zero target but scheduled coverage are still visible
                            const weighted = parts.map(p => ({ p, weight: ((p.target || 0) > 0 ? (p.target || 0) : ((p.scheduled || 0) > 0 ? (p.scheduled || 0) : 1)) }));
                            const totalWeight = weighted.reduce((s, x) => s + (Number(x.weight) || 0), 0) || 1;
                            return weighted.map(({ p, weight }) => {
                              const w = (weight / totalWeight) * 100;
                              const bg = colorFor(p.scheduled, p.target);
                              return <Box key={`seg-${p.id}`} sx={{ width: `${w}%`, bgcolor: bg, height: '100%', transition: 'width 220ms ease, background-color 220ms ease' }} />;
                            });
                          })() : (
                            <Box sx={{ width: '100%', bgcolor: colorFor(totalScheduled, totalTarget), height: '100%', transition: 'background-color 220ms ease' }} />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(employeesByDeptAndRole).map(([dept, rolesObj]) => (
                <React.Fragment key={dept}>
                  {/* Department header */}
                  <TableRow>
                    <TableCell colSpan={weekDays.length + 1} sx={{ bgcolor: 'common.black', color: 'common.white', py: 0.5, pl: 2, fontSize: '0.9rem' }}>
                      {deptNameMap && deptNameMap[dept] ? deptNameMap[dept] : dept}
                    </TableCell>
                  </TableRow>

                  {/* Roles within this department */}
                  {Object.entries(rolesObj || {}).map(([roleId, employees]) => {
                    const uniqueEmps = Array.from(new Map((employees || []).map(e => [e.id, e])).values());
                    const roleDoc = roleMap[roleId];
                    const sampleShift = (shifts || []).find(s => s.role === roleId);
                    const baseName = roleDoc?.name || sampleShift?.roleName || roleId;
                    // If role doc is missing (roles list filters archived out) but we have a shift
                    // that references the role, treat it as archived so UI shows the label clearly.
                    const isArchived = !!roleDoc?.archived || (!roleDoc && !!sampleShift) || !!sampleShift?.archived;
                    const roleName = isArchived ? `${baseName} (Archived)` : baseName;
                    const roleColor = roleDoc?.color || '#888';

                    return (
                      <React.Fragment key={`${dept}-${roleId}`}>
                        {/* Role header */}
                        <TableRow>
                          <TableCell colSpan={weekDays.length + 1} sx={{ bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(roleColor, 0.14) : alpha(roleColor, 0.12)), borderLeft: `4px solid ${roleColor}`, py: 0.5, pl: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{roleName}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                <Tooltip title="Employees in this role"><GroupIcon fontSize="small" sx={{ color: 'text.secondary' }} /></Tooltip>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{uniqueEmps.length}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Open shifts row */}
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: 'primary.main', fontStyle: 'italic', pl: 4, borderLeft: `4px solid ${roleColor}`, bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(roleColor, 0.06) : alpha(roleColor, 0.04)) }}>
                            Open Shifts
                          </TableCell>
                          {weekDays.map(d => {
                            const dayKey = toIsoDateString(d);
                            const dayOpenShifts = (openShiftsByDeptAndRole[dept]?.[roleId] || []).filter(s => {
                              const { localDate } = extractShiftLocalTime(s);
                              return localDate === dayKey;
                            });
                            const droppableId = getDroppableId('openshift', dept, roleId, dayKey);
                              return (
                              <DroppableCell key={toIsoDateString(d)} id={droppableId} align="center" forceOver={isDragging && currentOverId === droppableId} onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }} openCellMenu={(e) => openMenu(e, null, d, dayOpenShifts[0] || null, dept, roleId)} openDialogImmediate={(e) => openDialogImmediate ? openDialogImmediate(e, null, d, null, dept, roleId) : null} sx={{ position: 'relative', minHeight: 36, minWidth: 120, bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(roleColor, 0.06) : alpha(roleColor, 0.04)) }}>
                                  {dayOpenShifts.map(s => (
                                  <DraggableShift key={s.id} shift={s} roleMap={roleMap} disabled={disabled} isWeekPublished={isWeekPublished} activeShiftId={activeShift?.id} parentIsDragging={isDragging} forceEndDrag={forceEndDrag} forceSuspendDrag={forceSuspendDrag} availability={availability} conflictMode={conflictMode} onEditClick={(e, sh) => { try { e.stopPropagation(); e.preventDefault(); } catch (err) {} if (typeof openDialogImmediate === 'function') openDialogImmediate(e, null, d, sh, dept, roleId); else if (typeof openMenuImmediate === 'function') openMenuImmediate(null, d, sh, dept, roleId, e?.clientX || null, e?.clientY || null, 'pencil'); else openMenu(e, null, d, sh, dept, roleId); }} openDialogImmediate={(e, sh) => openDialogImmediate ? openDialogImmediate(e, null, d, sh, dept, roleId) : null} />
                                ))}
                              </DroppableCell>
                            );
                          })}
                        </TableRow>

                        {/* Employee rows */}
                        {uniqueEmps.map(emp => {
                          // total minutes across all departments/roles for this employee (what we show under the name)
                          const empShifts = assignedShifts.filter(s => s.employeeId === emp.id);
                          const totalWeeklyMinutes = empShifts.reduce((sum, s) => {
                            const { startTime: start, endTime: end } = extractShiftLocalTime(s);
                            const toMinutes = (t) => { if (!t) return 0; const parts = String(t).split(':'); const h = parseInt(parts[0], 10) || 0; const m = parseInt(parts[1], 10) || 0; return h * 60 + m; };
                            const startMin = toMinutes(start); const endMin = toMinutes(end);
                            const duration = endMin >= startMin ? (endMin - startMin) : (endMin + 24*60 - startMin);
                            return sum + (isNaN(duration) ? 0 : duration);
                          }, 0);

                          // Build per-day intervals and scheduled-by-day to find overlaps
                          const intervalsByDay = {}; // dayKey -> [[s,e],...]
                          const scheduledByDay = {}; // dayKey -> minutes scheduled (counts overlaps)
                          empShifts.forEach(s => {
                            const { localDate: dayKey } = extractShiftLocalTime(s);
                            const { startTime: start, endTime: end } = extractShiftLocalTime(s);
                            const toMinutes = (t) => { if (!t) return 0; const parts = String(t).split(':'); const h = parseInt(parts[0], 10) || 0; const m = parseInt(parts[1], 10) || 0; return h * 60 + m; };
                            const startMin = toMinutes(start); const endMin = toMinutes(end);
                            if (isNaN(startMin) || isNaN(endMin)) return;
                            // only include days within current week
                            const inWeek = weekDays.some(d => toIsoDateString(d) === dayKey);
                            if (!inWeek) return;
                            intervalsByDay[dayKey] = intervalsByDay[dayKey] || [];
                            scheduledByDay[dayKey] = scheduledByDay[dayKey] || 0;
                            if (endMin >= startMin) {
                              intervalsByDay[dayKey].push([startMin, endMin]);
                              scheduledByDay[dayKey] += (endMin - startMin);
                            } else {
                              // overnight split
                              intervalsByDay[dayKey].push([startMin, 24 * 60]);
                              scheduledByDay[dayKey] += (24 * 60 - startMin);
                              const nextDate = toDate(dayKey) || new Date(dayKey);
                              nextDate.setDate(nextDate.getDate() + 1);
                              const nextKey = toIsoDateString(nextDate);
                              if (weekDays.some(d => toIsoDateString(d) === nextKey)) {
                                intervalsByDay[nextKey] = intervalsByDay[nextKey] || [];
                                intervalsByDay[nextKey].push([0, endMin]);
                                scheduledByDay[nextKey] = scheduledByDay[nextKey] || 0;
                                scheduledByDay[nextKey] += endMin;
                              }
                            }
                          });

                          const mergeAndLength = (list) => {
                            if (!list || list.length === 0) return 0;
                            const sorted = list.slice().sort((a,b) => a[0]-b[0]);
                            const merged = [sorted[0].slice()];
                            for (let i=1;i<sorted.length;i++){
                              const [s,e] = sorted[i];
                              const last = merged[merged.length-1];
                              if (s <= last[1]) last[1] = Math.max(last[1], e);
                              else merged.push([s,e]);
                            }
                            return merged.reduce((sum,[s,e]) => sum + (e-s), 0);
                          };

                          let uniqueMinutes = 0;
                          let overlapMinutes = 0;
                          Object.keys(intervalsByDay).forEach(dayKey => {
                            const uniqueDay = mergeAndLength(intervalsByDay[dayKey]);
                            const scheduledDay = scheduledByDay[dayKey] || 0;
                            uniqueMinutes += uniqueDay;
                            overlapMinutes += Math.max(0, scheduledDay - uniqueDay);
                          });

                          const totalWeeklyHours = totalWeeklyMinutes / 60;
                          const uniqueHours = uniqueMinutes / 60;
                          const overlapHours = overlapMinutes / 60;
                          const baseOT = Math.max(0, uniqueHours - 40);
                          const empOT = baseOT + overlapHours;
                          const overtimeHours = empOT; // includes overlap minutes

                          const isScheduledOutsideAvailability = (() => {
                            try {
                              const empWeekShifts = empShifts || [];
                              for (const s of empWeekShifts) {
                                if (!s || s.deleted || s.isOpenShift) continue;
                                const shiftDate = s.shiftDateKey ? toDate(s.shiftDateKey) : toDate(s.shiftDate);
                                const dayKey = toIsoDateString(shiftDate);
                                if (!weekDays.some(d => toIsoDateString(d) === dayKey)) continue;
                                const start = s.shiftStartTime || (s.startTime || '00:00');
                                const end = s.shiftEndTime || (s.endTime || '00:00');
                                if (typeof isEmployeeAvailable === 'function') {
                                  const dayOfWeek = shiftDate.getDay();
                                  const availArr = Array.isArray(availability) ? availability : [];
                                  const available = isEmployeeAvailable(availArr, emp.id, dayOfWeek, start, end);
                                  if (!available) return true;
                                }
                              }
                            } catch (err) {}
                            return false;
                          })();

                          return (
                            <TableRow key={emp.id} hover sx={{ bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(roleColor, 0.06) : alpha(roleColor, 0.04)) }}>
                              <TableCell sx={{ pl: 4, borderLeft: `4px solid ${roleColor}`, borderRight: (theme) => `1px solid ${theme.palette.divider}`, ...(isScheduledOutsideAvailability && { bgcolor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.14 : 0.18) }) }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Link component="button" underline="always" onClick={(e) => { e.stopPropagation(); setQuickViewEmployee(emp); setQuickViewOpen(true); }} sx={{ padding: 0, minWidth: 0, justifyContent: 'flex-start', display: 'inline-flex', alignItems: 'center' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                        <Typography sx={{ fontWeight: 500, color: 'text.primary', cursor: 'pointer' }}>{emp.name || [emp.firstName, emp.lastName].filter(Boolean).join(' ')}</Typography>
                                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>{typeof totalWeeklyHours === 'number' ? totalWeeklyHours.toFixed(2) : '0.00'} hrs</Typography>
                                          {overtimeHours > 0 && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', fontWeight: 600, textAlign: 'center' }}>+{overtimeHours.toFixed(2)} hrs OT</Typography>
                                          )}
                                        </Box>
                                      </Box>
                                  </Link>
                                </Box>
                              </TableCell>
                              {weekDays.map(d => {
                                const dayKey = toIsoDateString(d);
                                const allForEmpDay = assignedShifts.filter(s => {
                                  if (s.dept !== dept || s.employeeId !== emp.id) return false;
                                  const shiftDateKey = s.shiftDateKey || toIsoDateString(s.shiftDate);
                                  return shiftDateKey === dayKey;
                                });

                                const normalizeRoleId = (s) => {
                                  if (!s || !s.role) return null;
                                  if (roleMap[s.role]) return s.role;
                                  const byName = Object.keys(roleMap).find(k => (roleMap[k].name || '').toLowerCase() === String(s.role).toLowerCase());
                                  if (byName) return byName;
                                  return s.role;
                                };

                                const assignedHere = allForEmpDay.filter(s => normalizeRoleId(s) === roleId);
                                const ghostHere = allForEmpDay.filter(s => normalizeRoleId(s) !== roleId);
                                const droppableId = getDroppableId('employee', emp.id, dayKey, dept, roleId);
                                return (
                                  <DroppableCell key={toIsoDateString(d)} id={droppableId} align="center" isDragging={isDragging} forceOver={isDragging && currentOverId === droppableId} onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }} openCellMenu={(e) => openMenu(e, emp, d, (assignedHere[0] || ghostHere[0]) || null, dept, roleId)} openDialogImmediate={(e) => openDialogImmediate ? openDialogImmediate(e, emp, d, null, dept, roleId) : null} sx={{ position: 'relative', minHeight: 44, minWidth: 120, bgcolor: (theme) => (theme.palette.mode === 'dark' ? alpha(roleColor, 0.06) : alpha(roleColor, 0.04)) }}>
                                    {assignedHere.map(s => (
                                      <DraggableShift key={s.id} shift={s} roleMap={roleMap} disabled={disabled} isWeekPublished={isWeekPublished} activeShiftId={activeShift?.id} parentIsDragging={isDragging} forceEndDrag={forceEndDrag} forceSuspendDrag={forceSuspendDrag} availability={availability} conflictMode={conflictMode} onEditClick={(e, sh) => { try { e.stopPropagation(); e.preventDefault(); } catch (err) {} if (typeof openDialogImmediate === 'function') openDialogImmediate(e, emp, d, sh, dept, roleId); else if (typeof openMenuImmediate === 'function') openMenuImmediate(emp, d, sh, dept, roleId, e?.clientX || null, e?.clientY || null, 'pencil'); else openMenu(e, emp, d, sh, dept, roleId); }} openDialogImmediate={(e, sh) => openDialogImmediate ? openDialogImmediate(e, emp, d, sh, dept, roleId) : null} />
                                    ))}
                                    {/* Ghost shifts: show non-interactive gray boxes for shifts the employee has in other roles/locations on the same day */}
                                    {ghostHere.map(s => {
                                      const roleName = roleMap[s.role]?.name || s.roleName || s.role || 'Role';
                                      // Prefer explicit locationName on shift, fallback to mapped location name via locationId
                                      const rawLocationId = s.locationId || s.location;
                                      const mappedLocation = rawLocationId ? (locationMap[rawLocationId] && locationMap[rawLocationId].name) : null;
                                      const locationLabel = s.locationName || mappedLocation || rawLocationId || '';
                                      const start = s.shiftStartTime || (s.startTime || '00:00');
                                      const end = s.shiftEndTime || (s.endTime || '00:00');
                                              return (
                                                <Box
                                                  key={`ghost-${s.id}`}
                                                  sx={{
                                                    mb: 0.4,
                                                    p: 0.35,
                                                    borderRadius: 1,
                                                    bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
                                                    color: (theme) => theme.palette.text.primary,
                                                    border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                                                    pointerEvents: 'none',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                    textAlign: 'left',
                                                    display: 'block',
                                                  }}
                                                >
                                                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'left', lineHeight: 1, color: 'text.secondary' }}>{formatTime(start)} – {formatTime(end)}</Typography>
                                                  <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'left', lineHeight: 1 }}>{`${roleName}${locationLabel ? ` @ ${locationLabel}` : ''}`}</Typography>
                                                </Box>
                                              );
                                    })}
                                  </DroppableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableBody>
            <TableFooter>
            {/* Sticky analytics footer row: shows Scheduled Labor % per day and stays visible while scrolling */}
            <TableRow sx={{ bgcolor: '#FFF8E1' }}>
              <TableCell sx={{ borderRight: (theme) => `1px solid ${theme.palette.divider}`, position: 'sticky', left: 0, bottom: 0, zIndex: 1300, bgcolor: '#FFF8E1', fontWeight: 700 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" variant="outlined" sx={{ minWidth: 160 }}>
                    <Select
                      value={footerMode}
                      onChange={(e) => setFooterMode(e.target.value)}
                      sx={{ height: 30 }}
                    >
                      <MenuItem value="percent">Scheduled Labor %</MenuItem>
                      <MenuItem value="dollars">Scheduled Labor $</MenuItem>
                      <MenuItem value="hours">Scheduled Hours</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </TableCell>
              {weekDays.map((d, i) => {
                const key = toIsoDateString(d);
                if (footerMode === 'percent') {
                  const pct = analytics && analytics.laborPct && typeof analytics.laborPct[key] !== 'undefined' ? Number(analytics.laborPct[key]) : null;
                  return (
                    <TableCell
                      key={`footer-${key}`}
                      align="center"
                      sx={{ position: 'sticky', bottom: 0, zIndex: 1200, bgcolor: '#FFF8E1', ...{ ...{ textAlign: 'center' } } }}
                    >
                      {pct !== null && !isNaN(pct) ? pct.toFixed(2) + '%' : '0.00%'}
                    </TableCell>
                  );
                }
                if (footerMode === 'hours') {
                  // Prefer analytics.scheduledHours if provided, else compute from assignedShifts
                  const hrs = (analytics && analytics.scheduledHours && typeof analytics.scheduledHours[key] !== 'undefined')
                    ? Number(analytics.scheduledHours[key])
                    : (() => {
                      // sum durations for assignedShifts on this day
                      try {
                        const shiftsForDay = assignedShifts.filter(s => {
                          if (!s || s.deleted || s.isOpenShift) return false;
                          const sd = s.shiftDateKey ? toDate(s.shiftDateKey) : toDate(s.shiftDate);
                          return toIsoDateString(sd) === key;
                        });
                        let minutes = 0;
                        shiftsForDay.forEach(s => {
                          const start = s.shiftStartTime || s.startTime || '00:00';
                          const end = s.shiftEndTime || s.endTime || '00:00';
                          const [sh, sm] = (start || '00:00').split(':').map(Number);
                          const [eh, em] = (end || '00:00').split(':').map(Number);
                          let startMin = (Number.isFinite(sh) ? sh : 0) * 60 + (Number.isFinite(sm) ? sm : 0);
                          let endMin = (Number.isFinite(eh) ? eh : 0) * 60 + (Number.isFinite(em) ? em : 0);
                          if (endMin === 0) endMin = 24 * 60;
                          // handle overnight
                          if (endMin <= startMin) endMin += 24 * 60;
                          minutes += Math.max(0, endMin - startMin);
                        });
                        return minutes / 60;
                      } catch (err) { return 0; }
                    })();
                  return (
                    <TableCell
                        key={`footer-${key}`}
                        align="center"
                        sx={{ position: 'sticky', bottom: 0, zIndex: 1200, bgcolor: '#FFF8E1', ...{ ...{ textAlign: 'center' } } }}
                      >
                      {typeof hrs === 'number' && !isNaN(hrs) ? hrs.toFixed(2) + ' hrs' : '0.00 hrs'}
                    </TableCell>
                  );
                }
                const cost = analytics && analytics.laborCost && typeof analytics.laborCost[key] !== 'undefined' ? Number(analytics.laborCost[key]) : null;
                return (
                  <TableCell
                      key={`footer-${key}`}
                      align="center"
                      sx={{ position: 'sticky', bottom: 0, zIndex: 1200, bgcolor: '#FFF8E1', ...{ ...{ textAlign: 'center' } } }}
                    >
                    {cost !== null && !isNaN(cost)
                      ? cost.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                      : '$0.00'}
                  </TableCell>
                );
              })}
            </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

  {/* Using the original DOM element for drag visuals; no DragOverlay to avoid duplicates */}
      <DragOverlay dropAnimation={null}>
        {isDragging && activeShift ? (
          // Render a temporary copy of the active shift marked as unpublished
          // so users see the move as an unpublished change (yellow) while
          // dragging a published shift to a new spot.
          <DraggableShift
            shift={{ ...activeShift, isPublished: false, pendingCreate: true }}
            roleMap={roleMap}
            isDragOverlay={true}
            disabled={disabled}
            isWeekPublished={isWeekPublished}
            activeShiftId={activeShift?.id}
            overlayPending={true}
            availability={availability}
            conflictMode={conflictMode}
          />
        ) : null}
      </DragOverlay>
      {/* Employee Quick View Dialog */}
      <EmployeeQuickView
        employee={quickViewEmployee}
        open={quickViewOpen}
        onClose={() => { setQuickViewOpen(false); setQuickViewEmployee(null); }}
        locations={[]}
        scheduleConflicts={[]}
      />
    </DndContext>
  );
}
